import { cn } from "@/lib/utils";
import { useUserStore } from "@/store/user";
import { reuleaux } from "ldrs";
import { motion } from "motion/react";
import { useMemo } from "react";

reuleaux.register();

/** Set to true to restore the previous reuleaux spinner. */
const USE_LEGACY_LOADER = false;

const DEFAULT_WIDTH = 80;
const VIEWBOX_WIDTH = 23;
const VIEWBOX_HEIGHT = 12;

const ICON_PATHS = [
  "M20.6429 0.289999C20.4477 0.0999994 20.1908 0 19.9134 0H7.10028C6.82284 0 6.56596 0.109999 6.37073 0.289999L4.92193 1.7C4.7267 1.89 4.62394 2.14 4.62394 2.41V3H19.3174V7.5H22.4V2.41C22.4 2.14 22.287 1.89 22.102 1.7L20.6532 0.289999H20.6429Z",
  "M7.70642 9V4.5H4.62385V9.59C4.62385 9.86 4.73688 10.11 4.92184 10.3L6.37064 11.71C6.56587 11.9 6.82275 12 7.10018 12H19.9237C20.2011 12 20.458 11.89 20.6532 11.71L22.102 10.3C22.2972 10.11 22.4 9.86 22.4 9.59V9H7.70642Z",
  "M0 9.59C0 9.86 0.113028 10.11 0.297982 10.3L1.74679 11.71C1.94202 11.9 2.1989 12 2.47633 12H3.08257V4.5H0V9.59Z",
  "M1.74679 0.289999L0.297982 1.7C0.102752 1.89 0 2.14 0 2.41V3H3.08257V0H2.47633C2.1989 0 1.94202 0.109999 1.74679 0.289999Z"
];

type LoaderProps = {
  className?: string;
  color?: string;
  size?: number;
};

export const Loader: React.FC<LoaderProps> = ({ className = "", color, size: sizeProp }) => {
  const theme = useUserStore((state) => state.theme);
  const themeColor = theme === "dark" ? "#ffffff" : "#000000";
  const fillColor = color ?? themeColor;

  const width = useMemo(
    () => resolveLoaderWidth(className, sizeProp),
    [className, sizeProp]
  );

  if (USE_LEGACY_LOADER) {
    return (
      <LegacyReuleauxLoader color={fillColor} width={width} />
    );
  }

  const hasTailwindSizeClass = /!?[wh]-(?:\[[0-9]+px\]|\d+)/.test(className);

  return (
    <svg
      viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn("inline-block shrink-0", className)}
      style={
        sizeProp != null
          ? { width: sizeProp, height: "auto" }
          : hasTailwindSizeClass
            ? undefined
            : { width, height: "auto" }
      }
      data-testid="Loader"
      aria-hidden
    >
      {ICON_PATHS.map((d, i) => (
        <motion.path
          key={i}
          d={d}
          animate={{ opacity: [0.08, 1, 0.08] }}
          transition={{
            duration: 1.6,
            repeat: Infinity,
            delay: i * 0.18,
            ease: "easeInOut"
          }}
          fill={fillColor}
        />
      ))}
    </svg>
  );
};

function resolveLoaderWidth(className: string, sizeProp?: number): number {
  if (sizeProp != null && !isNaN(sizeProp)) {
    return sizeProp;
  }

  const bracketWidth = /!?w-\[([0-9]+)px\]/.exec(className);
  if (bracketWidth) {
    const parsed = parseInt(bracketWidth[1], 10);
    if (!isNaN(parsed)) return parsed;
  }

  const bracketHeight = /!?h-\[([0-9]+)px\]/.exec(className);
  if (bracketHeight) {
    const parsed = parseInt(bracketHeight[1], 10);
    if (!isNaN(parsed)) {
      return Math.round(parsed * (VIEWBOX_WIDTH / VIEWBOX_HEIGHT));
    }
  }

  const tailwindWidth = /\b!?w-(\d+)\b/.exec(className);
  if (tailwindWidth && !className.includes(`w-[${tailwindWidth[1]}`)) {
    const parsed = parseInt(tailwindWidth[1], 10);
    if (!isNaN(parsed)) return parsed * 4;
  }

  const tailwindHeight = /\b!?h-(\d+)\b/.exec(className);
  if (tailwindHeight && !className.includes(`h-[${tailwindHeight[1]}`)) {
    const parsed = parseInt(tailwindHeight[1], 10);
    if (!isNaN(parsed)) {
      return Math.round(parsed * 4 * (VIEWBOX_WIDTH / VIEWBOX_HEIGHT));
    }
  }

  return DEFAULT_WIDTH;
}

type LegacyReuleauxLoaderProps = {
  color: string;
  width: number;
};

const LegacyReuleauxLoader: React.FC<LegacyReuleauxLoaderProps> = ({
  color,
  width
}) => (
  <l-reuleaux
    size={width}
    stroke="5"
    stroke-length="0.15"
    bg-opacity="0.1"
    speed="0.7"
    color={color}
    data-testid="Loader"
  ></l-reuleaux>
);
